// ** Utils
import { normalizePhoneDIN5008 } from '@/utils/shared/stringUtils';

// ** Types
import type { User } from 'next-auth';
import type { BookingUser } from '@/types/userTypes';
import type { Event, CompanyData } from '@/types/graphql';
import type { Participant, EventParticipantFormikValues } from '@/types/formik';

type EventPrices = {
	memberPrice?: number;
	memberPriceAdditionalPerson?: number;
	notMemberPrice?: number;
	notMemberPriceAdditionalPerson?: number;
};

type PricingType = {
	memberPrice?: number;
	memberPriceAdditionalPerson?: number;
	notMemberPrice?: number;
	notMemberPriceAdditionalPerson?: number;
};

type BuildInitialValuesParams = {
	company: CompanyData;
	userData: BookingUser;
	currentUser: User;
	eventId: string | number;
	prices: PricingType;
	includeInvoice?: boolean;
	eventType?: string;
};

const assignValue = (obj: Record<string, any> | undefined, fieldPath: string, defaultValue: any = ''): string => {
	const fields = fieldPath.split('.');

	return fields.reduce((acc: any, field) => (acc && acc[field] !== null ? acc[field] : defaultValue), obj) as string;
};

export const calculateParticipantInitialPrice = (member: boolean, prices: EventPrices, index: number): number => {
	if (member) {
		if (index === 0) {
			return prices.memberPrice ?? 0;
		}
		return prices.memberPriceAdditionalPerson ?? prices.memberPrice ?? 0;
	} else {
		if (index === 0) {
			return prices.notMemberPrice ?? 0;
		}
		return prices.notMemberPriceAdditionalPerson ?? prices.notMemberPrice ?? 0;
	}
};

export const calculateParticipantPrices = (
	member: boolean,
	participants: Participant[],
	prices: EventPrices
): Participant[] => {
	return participants.map((participant, index) => {
		let price = 0;
		const isFirst = index === 0;

		if (member) {
			if (isFirst) {
				price = prices.memberPrice ?? prices.memberPriceAdditionalPerson ?? 0;
			} else {
				price = prices.memberPriceAdditionalPerson ?? prices.memberPrice ?? 0;
			}
		} else {
			if (isFirst) {
				price = prices.notMemberPrice ?? prices.notMemberPriceAdditionalPerson ?? 0;
			} else {
				price = prices.notMemberPriceAdditionalPerson ?? prices.notMemberPrice ?? 0;
			}
		}

		return {
			...participant,
			price
		};
	});
};

export const buildInitialValues = ({
	company,
	userData,
	currentUser,
	eventId,
	prices,
	includeInvoice = true,
	eventType
}: BuildInitialValuesParams): EventParticipantFormikValues => {
	return {
		eventId: Number(eventId),
		company: {
			companyName: assignValue(company, 'company.companyName'),
			contactData: {
				phone: normalizePhoneDIN5008(String(assignValue(company, 'company.contactData.phone'))),
				mobilePhone: normalizePhoneDIN5008(String(assignValue(company, 'company.contactData.mobilePhone'))),
				email: assignValue(company, 'company.contactData.email')
			},
			address: {
				street: assignValue(company, 'company.address.street'),
				houseNumber: assignValue(company, 'company.address.houseNumber'),
				postalCode: assignValue(company, 'company.address.postalCode'),
				city: assignValue(company, 'company.address.city'),
				stateCode: assignValue(company, 'company.address.stateCode')
			}
		},
		member: currentUser?.isMember ?? false,
		invoice: includeInvoice
			? {
					contact: assignValue(userData, 'company.companyName'),
					invoiceRecipient: assignValue(userData, 'company.companyName'),
					street: assignValue(userData, 'billing.invoiceAddress.street'),
					houseNumber: assignValue(userData, 'billing.invoiceAddress.houseNumber'),
					postalCode: assignValue(userData, 'billing.invoiceAddress.postalCode'),
					city: assignValue(userData, 'billing.invoiceAddress.city')
				}
			: undefined,
		participants: [
			{
				id: String(currentUser.cobraId),
				title: assignValue(userData, 'person.title'),
				salutation: assignValue(userData, 'person.salutation'),
				firstName: assignValue(userData, 'person.name.firstName'),
				lastName: assignValue(userData, 'person.name.lastName'),
				price: calculateParticipantInitialPrice(currentUser?.isMember ?? false, prices, 0),
				telephone: normalizePhoneDIN5008(String(assignValue(userData, 'person.contactData.phone'))),
				mobilePhone: normalizePhoneDIN5008(String(assignValue(userData, 'person.contactData.mobilePhone'))),
				email: assignValue(userData, 'person.contactData.email'),
				notes: '',
				...(eventType !== 'Webinar' && { foodNotes: [] })
			}
		],
		extraParticipants: '',
		acceptTerms: false
	};
};

export const extractEventDetails = (eventById: Event) => {
	return {
		id: eventById.id,
		title: eventById.title,
		eventNumber: eventById.eventNumber,
		appointmentInfo: eventById.appointmentInfo,
		timeOfDay: eventById.timeOfDay,
		memberPrice: eventById.memberPrice,
		memberPriceAdditionalPerson: eventById.memberPriceAdditionalPerson,
		notMemberPrice: eventById.notMemberPrice,
		notMemberPriceAdditionalPerson: eventById.notMemberPriceAdditionalPerson,
		eventPlace: eventById.eventPlace,
		maxParticipantsNumber: eventById.maxParticipantsNumber,
		onlyForMembers: eventById.onlyForMembers,
		priceWithoutTax: eventById.priceWithoutTax,
		subtitle: eventById.subtitle,
		field: eventById.field,
		eventType: eventById.eventType,
		priceCondition: eventById.priceCondition
	};
};

export const handleEventImage = (field: string) => {
	const validFields = [
		'Berufsbildung',
		'Bestatter',
		'Betriebstechnik',
		'Fachtagung',
		'Formgebung',
		'Gesundheit',
		'Mitarbeiter',
		'Neue Technologien',
		'Recht',
		'Sachverständige',
		'SIAM',
		'Treffpunkt',
		'Unternehmensführung',
		'Webinar'
	];

	return validFields.includes(field) ? `/images/cards/events/${field}.jpg` : `/images/cards/events/Webinar.jpg`;
};

export const handleGetResponsibleData = (responsibleData: any) => {
	const person = responsibleData?.person;

	const profileParts = [person?.salutation, person?.title, person?.name?.firstName, person?.name?.lastName].filter(
		Boolean
	);

	const profile = profileParts.join(' ').trim();

	return {
		profile: profile || undefined,
		email: person?.contactData?.email || undefined,
		phone: person?.contactData?.phone || undefined
	};
};
